import React, { useState, useEffect } from 'react';
import { OrderFile, OrderFileStatus, WCCustomer, WCOrder } from '../../types/wordpress';
import { WordPressAPI } from '../../services/api/api';
import OrderFileForm from './OrderFileForm';
import { Button, Table, Modal, message, Input, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    EditOutlined,
    SearchOutlined,
    PlayCircleOutlined,
    PaperClipOutlined,
} from '@ant-design/icons';
import { formatDate } from '../../utils/formatUtils';
import './styles.css';
import { useNavigate } from 'react-router-dom';
import { getStatusFilters, ORDER_FILE_STATUSES } from '../../constants/statuses';

const OrderFileList: React.FC = () => {
    const [orderFiles, setOrderFiles] = useState<OrderFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingOrderFile, setEditingOrderFile] = useState<OrderFile | null>(null);
    const [searchText, setSearchText] = useState('');
    const [customers, setCustomers] = useState<WCCustomer[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const navigate = useNavigate();

    useEffect(() => {
        fetchOrderFiles();
    }, [currentPage, pageSize, searchText]);

    const fetchOrderFiles = async () => {
        setLoading(true);
        try {
            const [orderFilesData, customersData] = await Promise.all([
                WordPressAPI.getOrderFiles({
                    page: currentPage,
                    per_page: pageSize,
                    search: searchText
                }),
                WordPressAPI.getCustomers()
            ]);

            if (Array.isArray(orderFilesData.items)) {
                setOrderFiles(orderFilesData.items);
                setTotal(orderFilesData.total);
            }

            if (Array.isArray(customersData.items)) {
                setCustomers(customersData.items);
            }
        } catch (error) {
            message.error('Failed to fetch data');
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (orderFile: OrderFile) => {
        setEditingOrderFile(orderFile);
        setIsModalVisible(true);
    };

    const handleAddNew = () => {
        setEditingOrderFile(null);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingOrderFile(null);
    };

    const handleFormSubmit = async (values: Partial<OrderFile>) => {
        try {
            setLoading(true);
            if (editingOrderFile?.id) {
                await WordPressAPI.updateOrderFile(editingOrderFile.id, values);
                message.success('Order file updated successfully');
            } else {
                await WordPressAPI.createOrderFile(values);
                message.success('Order file created successfully');
            }
            handleModalClose();
            await fetchOrderFiles();
        } catch (error) {
            message.error('Failed to save order file');
            console.error('Error saving order file:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartProcess = (record: OrderFile) => {
        if (record.order) {
            navigate(`/pallet_calculator/order-process/${record.order}`);
        } else {
            navigate(`/pallet_calculator/order-capacity/${record.id}`);
        }
    };

    const columns: ColumnsType<OrderFile> = [
        {
            title: 'Title',
            dataIndex: ['title', 'rendered'],
            key: 'title',
            render: (text: string, record: OrderFile) => (
                <div className="flex items-center gap-2">
                    <span>{text}</span>
                    {record.order_file_attachments?.length > 0 && (
                        <Tooltip title={`${record.order_file_attachments.length} attachments`}>
                            <PaperClipOutlined className="text-gray-500" />
                        </Tooltip>
                    )}
                </div>
            ),
            sorter: (a, b) => a.title.rendered.localeCompare(b.title.rendered),
        },
        {
            title: 'Customer',
            dataIndex: ['customer', 'display_name'],
            key: 'customer',
            render: (_, record) => (
                <span>{record.customer?.display_name || '-'}</span>
            ),
        },
        {
            title: 'Order',
            dataIndex: 'order',
            key: 'order',
            render: (order) => order ? `#${order}` : '-',
        },
        {
            title: 'Created',
            dataIndex: 'date_created',
            key: 'date_created',
            render: (date: string) => formatDate(date),
            sorter: (a, b) => new Date(a.date_created).getTime() - new Date(b.date_created).getTime(),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const statusConfig = ORDER_FILE_STATUSES.find(s => s.value === status);
                return (
                    <Tag color={statusConfig?.color || 'default'}>
                        {statusConfig?.label || status}
                    </Tag>
                );
            },
            filters: getStatusFilters(),
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <span className="flex flex-col sm:flex-row gap-2">
                    <Button
                        type="primary"
                        ghost
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        className="whitespace-nowrap"
                    >
                        Edit
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={() => handleStartProcess(record)}
                        className="whitespace-nowrap"
                    >
                        {record.order ? 'Continue Process' : 'Start Process'}
                    </Button>
                </span>
            ),
        },
    ];

    return (
        <div className="w-full p-4">
            <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold">Order Files</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <Input
                        placeholder="Search order files..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        className="w-full sm:w-[300px]"
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleAddNew}
                        className="whitespace-nowrap"
                    >
                        Add New Order File
                    </Button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table
                    columns={columns}
                    dataSource={orderFiles}
                    loading={loading}
                    rowKey="id"
                    rowClassName={(record) => record.is_deleted ? 'opacity-50' : ''}
                    pagination={{
                        total: total,
                        current: currentPage,
                        pageSize: pageSize,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </div>

            <Modal
                title={editingOrderFile ? 'Edit Order File' : 'Add New Order File'}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                className="responsive-modal"
                destroyOnClose
            >
                <OrderFileForm
                    key={editingOrderFile?.id || 'new'}
                    initialValues={editingOrderFile || undefined}
                    onSubmit={handleFormSubmit}
                    onCancel={handleModalClose}
                    customers={customers}
                    loading={loading}
                />
            </Modal>
        </div>
    );
};

export default OrderFileList;